// 同じキャンバスに参加しているユーザーのプレゼンス（一覧 + カーソル位置 + 表示ビュー + フォーカス状態）を
// 管理する Zustand store。useRealtimeConnection が viewer.snapshot / viewer.cursor / viewer.view を購読して
// このストアを更新する。

import { create } from "zustand";
import type { Viewer } from "@refinement-canvas/domain";

export interface ViewerCursor {
  readonly xRatio: number;
  readonly yRatio: number;
  readonly updatedAt: number;
}

export interface ViewerView {
  readonly panX: number;
  readonly panY: number;
  readonly zoom: number;
  readonly updatedAt: number;
}

interface CanvasViewersState {
  /** 自分以外を含む全参加者（自分の userId は除外する想定だが、サーバ側で同梱されている場合もある） */
  viewers: Record<string, Viewer>;
  /** userId -> 最新カーソル位置 */
  cursors: Record<string, ViewerCursor>;
  /** userId -> 最新ビュー（pan/zoom）。フォーカスモードで追尾する元データとして使用する。 */
  views: Record<string, ViewerView>;
  /**
   * フォーカスモード中のターゲット userId。null のとき非フォーカス（通常状態）。
   * フォーカス中は対象ユーザーの最新 view を画面に反映する一方、自分が操作を行った時点で解除する。
   */
  focusedUserId: string | null;

  setViewers: (viewers: readonly Viewer[]) => void;
  setCursor: (userId: string, cursor: ViewerCursor | null) => void;
  setView: (userId: string, view: ViewerView | null) => void;
  setFocusedUserId: (userId: string | null) => void;
  reset: () => void;
}

export const useCanvasViewersStore = create<CanvasViewersState>((set) => ({
  viewers: {},
  cursors: {},
  views: {},
  focusedUserId: null,
  setViewers: (viewers) =>
    set(() => ({
      viewers: Object.fromEntries(viewers.map((v) => [v.userId, v])),
    })),
  setCursor: (userId, cursor) =>
    set((state) => {
      if (cursor === null) {
        const next = { ...state.cursors };
        delete next[userId];
        return { cursors: next };
      }
      return { cursors: { ...state.cursors, [userId]: cursor } };
    }),
  setView: (userId, view) =>
    set((state) => {
      if (view === null) {
        const next = { ...state.views };
        delete next[userId];
        return { views: next };
      }
      return { views: { ...state.views, [userId]: view } };
    }),
  setFocusedUserId: (focusedUserId) => set({ focusedUserId }),
  reset: () => set({ viewers: {}, cursors: {}, views: {}, focusedUserId: null }),
}));
